import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Dropdown from 'react-dropdown';
import Loader from 'react-loader-spinner';

import Layout from '@components/layout';
import SEO from '@components/seo';
import { InsightPreviewCard } from '@components/card/insightPreviewCard';
import BackgroundSlash from '@components/backgroundSlash';
import Tooltip from '@components/elements/tooltip';
import Section from '@components/elements/section';
import Content from '@components/elements/content';
import LoadMoreButton from '@components/elements/loadmore-button';
import SearchIcon from '@components/icons/searchIcon';
import GridIcon from '@components/icons/gridMode';
import ListIcon from '@components/icons/listMode';
import ArrowIcon from '@components/icons/downArrow';
import UpArrow from '@components/icons/upArrow';
import moduleHeroConductor from '@modules/hero-module/module-hero-conductor';
import moduleConductor from '../modules/module-conductor';
// import { types, industries } from '@config';
import { mixins, media, theme } from '@styles';

const { colors } = theme;

export const pageQuery = graphql`
  {
    pageData: contentfulTemplatePage(contentful_id: { eq: "5bllEj45wYJtZITKsetOEe" }) {
      id
      contentful_id
      slug
      url
      seo {
        ...GlobalSEOFragment
      }
      hero {
        ...ModuleHeroFragment
      }
      subMenuType
      sections {
        ...ModuleGlobalItemFragment
      }
      footer
    }

    insightQuery: allContentfulTemplateInsight(sort: { fields: publishDate, order: DESC }) {
      nodes {
        ...GlobalInsightFragment
      }
    }
    featuredQuery: allContentfulTemplateInsight(
      filter: { location: { eq: "insights-page" } }
      sort: { fields: publishDate, order: DESC }
    ) {
      nodes {
        ...GlobalInsightFragment
      }
    }
  }
`;

const InsightPage = ({ location, data }) => {
  const {
    pageData: { seo, hero, footer, sections },
    insightQuery,
  } = data;

  const itemsLimit = 6;
  const heroSection = hero && moduleHeroConductor(hero, hero.type);
  const insights = insightQuery && insightQuery.nodes;
  const modules = sections && sections.map((module, index) => moduleConductor(module, index));

  const [currentPage, setPage] = useState(1);
  const [listView, setViewMode] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [types, setTypes] = useState(['ALL TYPE']);

  const [filters, setFilter] = useState({
    type: types[0],
    // industry: industries[0],
  });

  const [loading, setLoading] = useState(false);
  const [tootips, setTooltipOpen] = useState({
    grid: false,
    detail: false,
  });

  useEffect(() => {
    const insightsTypes = insights.map(item => item.type);
    const uniqueTypes = types
      .concat(insightsTypes)
      .filter((elem, index, self) => index === self.indexOf(elem));
    setTypes(uniqueTypes);
  }, []);

  const changeType = (item, type) => {
    setPage(1);
    setFilter({
      ...filters,
      [type]: item.value,
    });
  };

  const { pageUrl, pageTitle } = seo;

  const toggle = id => {
    setTooltipOpen({
      tootips,
      [id]: !tootips[id],
    });
  };

  const onSearch = ({ target }) => {
    setPage(1);
    setSearchKey(target.value);
  };

  const reSetSearchKey = () => {
    setPage(1);
    setSearchKey('');
  };

  let filteredResult = insights.filter(
    item =>
      filters['type'] === 'ALL TYPE' ||
      item['type'].toLowerCase() === filters['type'].toLowerCase(),
  );
  // .filter(
  //   item =>
  //     filters['industry'] === 'All INDUSTRIES' ||
  //     JSON.stringify(item['industry']).toLowerCase().includes(filters['industry'].toLowerCase()),
  // );

  if (searchKey) {
    filteredResult = filteredResult.filter(item => {
      const values = JSON.stringify(Object.values(item));
      return values.toLowerCase().includes(searchKey.toLowerCase());
    });
  }

  const pagesCount = Math.ceil(filteredResult.length / itemsLimit);
  const pageItmes = filteredResult.slice(0, (currentPage - 1) * itemsLimit + itemsLimit);
  const rows = Math.ceil(pageItmes.length / 2);

  const loadMore = () => {
    setLoading(true);
    return setTimeout(() => {
      setPage(currentPage < pagesCount ? currentPage + 1 : currentPage);
      setLoading(() => false);
    }, 800);
  };

  const breadcrumbs = [
    {
      pageUrl: pageUrl,
      pageTitle: pageTitle,
    },
  ];

  return (
    <Layout location={location} footer={footer}>
      {seo && <SEO data={seo} location={location} breadcrumbs={breadcrumbs} />}
      <StyledHeroSection>
        {heroSection}
        <BackgroundSlash bgColor="#5215fce6" />
      </StyledHeroSection>

      <StyledSlashSection>
        <BackgroundSlash />
        <Section>
          <StyledGridContent size="medium">
            <StyledArticleHeader className="insights-header">
              <StyledSearchViewModeWrapp>
                <StyledSearchField>
                  <SearchIcon />
                  <StyledSearchInput
                    type="text"
                    onChange={onSearch}
                    placeholder="Search Insights"
                    value={searchKey}
                  />
                  <CloseIcon active={searchKey} onClick={reSetSearchKey} />
                </StyledSearchField>
                <StyledViewMode>
                  <StyledModeItem
                    id="show-grid-btn"
                    listMode={!listView}
                    onClick={() => setViewMode(false)}>
                    <GridIcon />
                  </StyledModeItem>
                  <Tooltip
                    placement={'top'}
                    isOpen={tootips.grid}
                    target="show-grid-btn"
                    toggle={() => toggle('grid')}>
                    Show Grid
                  </Tooltip>
                  <StyledModeItem
                    id="show-detail-btn"
                    listMode={listView}
                    onClick={() => setViewMode(true)}>
                    <ListIcon />
                  </StyledModeItem>
                  <Tooltip
                    placement={'top'}
                    isOpen={tootips.detail}
                    target="show-detail-btn"
                    toggle={() => toggle('detail')}>
                    Show Details
                  </Tooltip>
                </StyledViewMode>
              </StyledSearchViewModeWrapp>
              <StyledFilterList>
                <StyledFilterItem>
                  <StyledTitle>TYPE</StyledTitle>
                  <StyledDropdownList
                    options={types}
                    onChange={e => changeType(e, 'type')}
                    value={filters['type']}
                    placeholder="Select an option"
                    arrowClosed={<ArrowIcon />}
                    arrowOpen={<UpArrow />}
                  />
                </StyledFilterItem>
                {/* <StyledFilterItem>
                  <StyledTitle>INDUSTRIES</StyledTitle>
                  <StyledDropdownList
                    options={industries}
                    onChange={e => changeType(e, 'industry')}
                    value={filters['industry']}
                    placeholder="Select an option"
                    arrowClosed={<ArrowIcon />}
                    arrowOpen={<UpArrow />}
                  />
                </StyledFilterItem> */}
              </StyledFilterList>
            </StyledArticleHeader>
            <StyledArticleGrid>
              {Array(rows)
                .fill(null)
                .map((item, index) => {
                  return (
                    <StyledTwoGrid key={index} listMode={listView}>
                      <InsightPreviewCard
                        data={pageItmes[index * 2]}
                        type={'normal'}
                        states={filters}
                        listView={listView}
                      />
                      {pageItmes[index * 2 + 1] && (
                        <InsightPreviewCard
                          data={pageItmes[index * 2 + 1]}
                          type={'normal'}
                          states={filters}
                          listView={listView}
                        />
                      )}
                    </StyledTwoGrid>
                  );
                })}
            </StyledArticleGrid>

            {pagesCount > currentPage && (
              <StyledLoadMoreButton onClick={loadMore} disabled={loading}>
                {loading ? (
                  <>
                    Loading &nbsp;&nbsp;&nbsp;
                    <Loader type="ThreeDots" height={10} width={50} timeout={1000} />
                  </>
                ) : (
                  <>
                    Load More
                    <ArrowIcon />
                  </>
                )}
              </StyledLoadMoreButton>
            )}
          </StyledGridContent>
        </Section>
      </StyledSlashSection>
      {modules}
    </Layout>
  );
};

InsightPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default InsightPage;

const StyledHeroSection = styled.div`
  position: relative;
  overflow: hidden;
  display: block;

  div[class^='main-hero__StyledColorCover'] {
    width: 80%;
    transform: translateX(-24%) skewX(-30deg);

    ${media.desktop`
      width: 100%;
      transform: translateX(-15%) skewX(-17deg);
      min-width: 550px;
    `}
  }

  .slash-div {
    ${media.desktop`display: none;`};
  }
`;

const StyledSlashSection = styled.div`
  position: relative;
  overflow: hidden;
  display: block;
`;

const StyledGridContent = styled(Content)`
  padding-top: 120px;
  ${media.desktop`padding-top: 46px;`};
  ${media.tablet`padding-top:30px;`}
`;

const StyledArticleHeader = styled.div`
  padding: 75px 0 120px;
  ${media.giant`padding: 75px 0 97px;`};
  ${media.giant`padding: 105px 0 97px;`};
  ${media.tablet`padding: 45px 0 82px;`};
  padding-top: 0 !important;
`;

const StyledSearchViewModeWrapp = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledArticleGrid = styled.div`
  display: grid;
  min-height: 150px;
  row-gap: 90px;
  ${media.bigGiant`row-gap: 72px;`};
  ${media.giant`row-gap: 60px;`};
  ${media.tablet`row-gap: 45px;`};
`;

const StyledTwoGrid = styled.div`
  display: grid;
  gap: 45px 30px;

  &:nth-child(2n) {
    grid-template-columns: 4fr 6fr;
  }
  &:nth-child(2n + 1) {
    grid-template-columns: 6fr 4fr;
  }

  ${media.bigGiant`
    &:nth-child(2n) { grid-template-columns: 5fr 7fr; }
    &:nth-child(2n + 1) { grid-template-columns: 7fr 5fr; }
  `};

  ${media.desktop`grid-template-columns: 6fr 6fr;`};

  ${({ listMode }) =>
    listMode &&
    `
    grid-template-rows: 1fr;
    grid-template-columns: 1fr !important;
  `}

  ${media.tablet`grid-template-columns: 12fr !important;`};

  .image-content {
    height: 308px;
  }

  & > div {
    ${({ listMode }) =>
      listMode &&
      `
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 32px 60px;
    `};

    ${media.giant`grid-gap: 32px 40px;`};
    ${media.tablet`display: block;`};

    header {
      ${({ listMode }) =>
        listMode &&
        `
      display: flex;
      flex-direction: column;
      justify-content: center;
    `};
    }
  }

  header {
    padding: 10px 0;
    div[class^='insightPreviewCard__StyledCardTitle'] {
      padding-top: 15px;
    }
  }
`;

const StyledSearchField = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  flex: 10;

  svg {
    position: absolute;
    width: 30px;
    height: 30px;
    margin: 0 4px;
    transform: translateY(-4px);
    path {
      fill: ${colors.lightPurple};
    }
    ${media.bigGiant`
      width: 24px; 
      height: 24px;
      margin: 0 13px;
    `};
    ${media.tablet`
      width: 16px; 
      height: 16px;
      margin: 0 0px;
      transform: translateY(-2px);
  `};
  }
`;

const StyledSearchInput = styled.input`
  ${mixins.subtitle32_medium};
  border: 0;
  border-radius: 0 !important;
  border-bottom: 2px solid #262626;
  background: transparent;
  width: 100%;
  padding-left: 54px;
  &:hover,
  &:focus,
  &:active {
    outline: none;
    border-color: ${colors.lightPurple};
  }

  &::placeholder {
    color: rgba(26, 26, 26, 0.7);
    ${mixins.subtitle32_medium};
    ${media.tablet`font-size: 18px;`};
  }
  ${media.bigGiant`padding-left: 48px;`};
  ${media.tablet`padding-left: 24px;`};
`;

const CloseIcon = styled.div`
  display: ${({ active }) => (active ? 'block' : 'none')};
  position: absolute;
  transform: translateY(-4px);

  right: 0;
  cursor: pointer;
  width: 36px;
  height: 36px;
  &:before {
    content: '×';
    color: black;
    font-size: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-weight: 200;
  }
`;

const StyledLoadMoreButton = styled(LoadMoreButton)`
  margin-top: 64px;
  margin-bottom: 64px;
  ${media.tablet`margin-bottom: 32px; margin-top: 32px;`};
`;

const StyledViewMode = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  ${media.tablet`display: none;`}
`;

const StyledModeItem = styled.div`
  display: flex;
  height: fit-content;
  padding: 5px;
  border-radius: 20px;
  cursor: pointer;
  margin-left: 10px;
  position: relative;
  transition: all 0.2s ease-in-out;

  svg {
    width: 25px;
    height: 25px;
    path {
      transition: all 0.2s ease-in-out;
      fill: gray;
      ${({ listMode }) => listMode && `fill: black;`};
    }
  }

  &:hover {
    background: #80808034;
    svg path {
      fill: ${colors.black};
    }
  }

  ${({ listMode }) =>
    listMode &&
    `
    background: #80808034;
  `}
`;

const StyledFilterList = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding-top: 16px;
  gap: 99px;
  ${media.tablet`padding-top: 12px; gap: 0px; justify-content: space-between;`};
  ${media.thone`display: block;`};
`;

const StyledFilterItem = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTitle = styled.div`
  ${mixins.hyperlinkbold24_small};
  ${media.bigGiant`font-size: 20px;`};
  ${media.tablet`font-size: 16px;`};
  color: ${colors.black};
  padding-right: 20px;
`;

const StyledDropdownList = styled(Dropdown)`
  .Dropdown-control {
    ${mixins.textmd_medium};
    font-weight: bold !important;
    display: flex;
    padding: 8px 8px 8px 10px;
    color: ${colors.lightPurple};
    border: 0px;
    background: transparent;
    .Dropdown-arrow-wrapper {
      padding-left: 8px;
      svg {
        padding-left: 8px;
        path {
          fill: ${colors.lightPurple};
        }
        height: fit-content;
        width: auto;
        position: relative;
      }
      ${media.bigGiant`padding-left: 0px;`};
    }
  }
  .Dropdown-menu {
    font-size: 16px;
    width: 110%;
    left: 0;
    padding-right: -10px;
    .Dropdown-option {
      padding: 8px 13px;
      &.is-selected {
        font-weight: bold;
      }
    }
    ${media.bigGiant`font-size: 14px;`}
    ${media.bigDesktop`font-size: 12px;`}
  }
`;
